import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { api } from "../lib/api";

export type Theme = "Auto" | "Light" | "Dark";
export type Lang = "IT" | "EN";

export type Preferences = {
  theme: Theme;
  lang: Lang;
  beep: boolean;
  vibrate: boolean;
  push_enabled: boolean;
  low_stock_alert: boolean;
  transfer_alerts: boolean;
  autofocus_camera: boolean;
  auto_sync: boolean;
  low_stock_threshold: number;
  double_tap_stock_out: boolean;
};

const DEFAULTS: Preferences = {
  theme: "Auto",
  lang: "IT",
  beep: true,
  vibrate: true,
  push_enabled: true,
  low_stock_alert: true,
  transfer_alerts: true,
  autofocus_camera: true,
  auto_sync: true,
  low_stock_threshold: 5,
  double_tap_stock_out: true,
};

type State = {
  prefs: Preferences;
  loaded: boolean;
  set: <K extends keyof Preferences>(key: K, value: Preferences[K]) => void;
  patch: (partial: Partial<Preferences>) => void;
  hydrateFromServer: () => Promise<void>;
};

let pushTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePush(prefs: Preferences) {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    api.put("/api/operators/me/preferences", prefs).catch(() => {
      /* offline-tolerant: localStorage is the fallback */
    });
  }, 500);
}

export const usePrefs = create<State>()(
  persist(
    (set, get) => ({
      prefs: DEFAULTS,
      loaded: false,
      set: (key, value) => {
        const next = { ...get().prefs, [key]: value };
        set({ prefs: next });
        schedulePush(next);
      },
      patch: (partial) => {
        const next = { ...get().prefs, ...partial };
        set({ prefs: next });
        schedulePush(next);
      },
      hydrateFromServer: async () => {
        try {
          const { data } = await api.get<Partial<Preferences>>("/api/operators/me/preferences");
          set({ prefs: { ...DEFAULTS, ...get().prefs, ...data }, loaded: true });
        } catch {
          set({ loaded: true });
        }
      },
    }),
    {
      name: "tcg-inv-hub-prefs",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ prefs: s.prefs }),
    },
  ),
);
